import { useRef } from 'react';
import { FilterOperator, IRange, IUser, LeadCallMode, TranscriptionStatusFilter } from '../../../extViewmodels';
import { CustomerType } from '../../containers/Demos/constants';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { ISelectOption } from '../../../web/components/DeprecatedSelect';
import { FieldKey } from '../../viewModels/form';
import { ILeadServedOutcomeView } from '../../viewModels/leads/interfaces';
import {
	ILeadServedFilterCriteria,
	LeadServedFilterCriteriaProperty,
	RecentCallsViewModel,
} from '../../viewModels/leadserved';
import {
	formatDate,
	formatDealStages,
	formatDurationRange,
	formatOptional,
	formatOutcomes,
	formatSelect,
	formatTeams,
	formatUser,
	formatUsers,
} from '../utils/criteriaBuilder';
import { useStatesOnFetch } from '../utils/useStatesOnFetch';

export interface ILeadViewFilter {
	callDuration?: IRange<number>;
	category?: string;
	companyTag?: string;
	customerType?: CustomerType;
	date?: IRange<Date>;
	dealStages?: string[];
	dateType?: 'call-date' | 'demo-date';
	leadCallMode?: LeadCallMode;
	manager?: IUser;
	officeLocations?: string[];
	outcome?: string;
	outcomes?: ISelectOption<FieldKey>[];
	quotaTeams?: string[];
	recordingChannels?: string;
	team?: string;
	teams?: string[];
	transcriptionStatus?: ISelectOption<TranscriptionStatusFilter>;
	user?: IUser;
	users?: IUser[]; // New field for multi-user selection
	userRole?: string;
}
interface IParams {
	filter: ILeadViewFilter;
	pageSize?: number;
	shouldReset?: boolean;
	shouldNotTriggerLoading?: boolean;
}

export function filterToCriteria(filter: ILeadViewFilter): ILeadServedFilterCriteria[] {
	const criteria: ILeadServedFilterCriteria[] = [];

	// Use DealDemoPerformDate when dateType is 'demo-date', otherwise use Date (default behavior)
	const dateProperty =
		filter?.dateType === 'demo-date'
			? LeadServedFilterCriteriaProperty.DealDemoPerformDate
			: LeadServedFilterCriteriaProperty.Date;
	criteria.push(formatDate<LeadServedFilterCriteriaProperty>(dateProperty, filter?.date));
	criteria.push(
		formatOptional<LeadServedFilterCriteriaProperty>(LeadServedFilterCriteriaProperty.Category, filter?.category)
	);
	criteria.push(
		formatOptional<LeadServedFilterCriteriaProperty>(LeadServedFilterCriteriaProperty.CompanyTag, filter?.companyTag)
	);
	if (filter?.customerType === CustomerType.Upgrade) {
		criteria.push({
			property: LeadServedFilterCriteriaProperty.CompanyTag,
			value: 'Type: Customer',
		});
	} else if (filter?.customerType === CustomerType.New) {
		criteria.push({
			property: LeadServedFilterCriteriaProperty.CompanyTag,
			op: FilterOperator.Not,
			value: 'Type: Customer',
		});
	}
	criteria.push(
		formatOptional<LeadServedFilterCriteriaProperty>(LeadServedFilterCriteriaProperty.Outcome, filter?.outcome)
	);
	criteria.push(
		formatOptional<LeadServedFilterCriteriaProperty>(
			LeadServedFilterCriteriaProperty.RecordingChannels,
			filter?.recordingChannels
		)
	);

	criteria.push(formatOptional<LeadServedFilterCriteriaProperty>(LeadServedFilterCriteriaProperty.Team, filter?.team));
	criteria.push(formatTeams<LeadServedFilterCriteriaProperty>(LeadServedFilterCriteriaProperty.Team, filter?.teams));
	criteria.push(
		formatSelect<LeadServedFilterCriteriaProperty>(
			LeadServedFilterCriteriaProperty.TranscriptionStatus,
			filter?.transcriptionStatus
		)
	);
	criteria.push(
		formatOptional<LeadServedFilterCriteriaProperty>(LeadServedFilterCriteriaProperty.UserRole, filter?.userRole)
	);
	criteria.push(
		formatOptional<LeadServedFilterCriteriaProperty>(
			LeadServedFilterCriteriaProperty.LeadCallMode,
			filter?.leadCallMode
		)
	);
	criteria.push(
		formatOutcomes<LeadServedFilterCriteriaProperty>(LeadServedFilterCriteriaProperty.Outcome, filter?.outcomes)
	);
	criteria.push(
		formatUser<LeadServedFilterCriteriaProperty>(LeadServedFilterCriteriaProperty.Manager, filter?.manager)
	);

	// Prioritize multi-user selection over single user selection for backward compatibility
	if (filter?.users && filter.users.length > 0) {
		criteria.push(formatUsers<LeadServedFilterCriteriaProperty>(LeadServedFilterCriteriaProperty.User, filter.users));
	} else {
		criteria.push(formatUser<LeadServedFilterCriteriaProperty>(LeadServedFilterCriteriaProperty.User, filter?.user));
	}

	criteria.push(
		formatDealStages<LeadServedFilterCriteriaProperty>(LeadServedFilterCriteriaProperty.DealStage, filter?.dealStages)
	);
	criteria.push(
		formatDealStages<LeadServedFilterCriteriaProperty>(LeadServedFilterCriteriaProperty.QuotaTeam, filter?.quotaTeams)
	);
	criteria.push(
		formatDealStages<LeadServedFilterCriteriaProperty>(
			LeadServedFilterCriteriaProperty.OfficeLocation,
			filter?.officeLocations
		)
	);
	criteria.push(
		formatDurationRange<LeadServedFilterCriteriaProperty>(
			LeadServedFilterCriteriaProperty.CallDuration,
			filter?.callDuration
		)
	);

	return criteria.filter(item => !!item);
}

export const useLeadView = () => {
	const userSession = useUserSession();

	const apiCall = useRef(new RecentCallsViewModel(userSession)).current;

	const { state, handleWorking, handleResponse, reset } = useStatesOnFetch<ILeadServedOutcomeView[]>();

	const get = async ({ filter, pageSize = 25, shouldReset, shouldNotTriggerLoading }: IParams) => {
		handleWorking(shouldNotTriggerLoading);
		if (shouldReset) {
			apiCall.controller.reset();
		}

		const criteria = filterToCriteria(filter);

		try {
			await apiCall.controller.getNext({ criteria }, pageSize);
			return handleResponse({ values: apiCall.controller.fetchResults.toArray() });
		} catch (error) {
			// @ts-ignore
			throw new Error(error);
		}
	};

	return {
		...state,
		empty: !state?.data || state?.data?.length <= 0,
		get,
		reset,
		status: state?.data ? 'done' : state.status,
	};
};
